import { create } from "zustand";
import { Message, MessageType } from "../../apis/copilot/types.ts";
import { abortAllMessages } from "../../apis/copilot/copilot";

export enum CopilotWorkflow {
  "TAM" = "tam",
  "SAM" = "sam",
  "SOM" = "som",
  "COMPETITIVE_ANALYSIS" = "competitive_analysis",
}

interface CopilotState {
  workflow: CopilotWorkflow | null;
  isVisible: boolean;
  isLoading: boolean;
  threadId: string | null;
  messages: Message[];
  isMinimized: boolean;
  highlightedElement: string | null;
  isHistoryOpen: boolean;
  showCopilot: (workflow: CopilotWorkflow) => void;
  hideCopilot: () => void;
  togglePanel: () => void;
  setThreadId: (threadId: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setMessages: (
    messages: Message[] | ((current: Message[]) => Message[])
  ) => void;
  addLoadingMessage: () => void;
  removeLoadingMessage: () => void;
  toggleMinimized: () => void;
  highlightElement: (elementId: string | null) => void;
  toggleHistory: () => void;
  openHistory: () => void;
  closeHistory: () => void;
}

export const useCopilotStore = create<CopilotState>((set, get) => {
  return {
    workflow: null,
    isVisible: false,
    isLoading: false,
    messages: [],
    threadId: null,
    isMinimized: false,
    highlightedElement: null,
    isHistoryOpen: false,
    showCopilot: (workflow) =>
      set({ isVisible: true, workflow, isMinimized: false }),
    hideCopilot: () => {
      abortAllMessages();
      set({
        isVisible: false,
        workflow: null,
        threadId: null,
        messages: [],
        isMinimized: false,
        isHistoryOpen: false,
      });
    },
    togglePanel: () => set((state) => ({ isVisible: !state.isVisible })),
    setIsLoading: (isLoading: boolean) => {
      set({ isLoading });
      if (isLoading) {
        get().addLoadingMessage();
        return;
      }
      get().removeLoadingMessage();
    },
    setThreadId: (threadId: string) => set({ threadId }),
    setMessages: (arg) => {
      const unorderedMessages =
        typeof arg === "function" ? arg(get().messages) : arg;

      const loadingMessage = unorderedMessages.find(
        ({ type }) => type === MessageType.LOADING
      );

      const messages = loadingMessage
        ? [
            ...unorderedMessages.filter((m) => m.type !== MessageType.LOADING),
            loadingMessage,
          ]
        : unorderedMessages;
      set({
        messages,
      });
    },
    addLoadingMessage: () => {
      set((state) => ({
        messages: [
          ...state.messages,
          {
            type: MessageType.LOADING,
            id: "loading",
            payload: {
              content: "",
              timestamp: new Date(),
            },
            rate: null,
          },
        ],
      }));
    },
    removeLoadingMessage: () =>
      set((state) => ({
        messages: state.messages.filter(
          (message) => message.type !== MessageType.LOADING
        ),
      })),
    toggleMinimized: () =>
      set((state) => ({ isMinimized: !state.isMinimized })),
    highlightElement: (elementId) => {
      set(() => ({ highlightedElement: elementId }));
    },
    toggleHistory: () =>
      set((state) => ({ isHistoryOpen: !state.isHistoryOpen })),
    openHistory: () => set({ isHistoryOpen: true }),
    closeHistory: () => set({ isHistoryOpen: false }),
  };
});
